import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { VW_ERROR_CODES, VwAuthService } from 'vwappservices';
import { AppStateService } from '../../services/app-state.service';

interface FeatureSlide {
  icon: string;
  title: string;
  description: string;
}

const FEATURE_SLIDE_INTERVAL_MS = 5000;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  loginId = '';
  password = '';
  errorMessage = '';
  showPassword = false;
  currentYear = new Date().getFullYear();

  features: FeatureSlide[] = [
    {
      icon: 'fa-bolt',
      title: 'Real-time production logging',
      description: 'Operators log every completed step instantly — check-counts or timed operations.',
    },
    {
      icon: 'fa-tachometer-alt',
      title: 'Live dashboards',
      description: 'Target, finished units, and pending work update the moment it happens on the floor.',
    },
    {
      icon: 'fa-sitemap',
      title: 'Enforced workflow sequence',
      description: 'Steps respect real dependencies — no step starts before what it depends on is done.',
    },
    {
      icon: 'fa-sun',
      title: 'Built for the shop floor',
      description: 'High-contrast UI designed to stay readable in bright, variable factory lighting.',
    },
  ];
  activeFeature = 0;
  private featureTimer?: ReturnType<typeof setInterval>;

  constructor(
    private route: Router,
    private auth: VwAuthService,
    private appState: AppStateService,
  ) {}

  ngOnInit(): void {
    this.startFeatureRotation();
  }

  ngOnDestroy(): void {
    if (this.featureTimer) clearInterval(this.featureTimer);
  }

  selectFeature(index: number) {
    this.activeFeature = index;
    this.startFeatureRotation();
  }

  private startFeatureRotation() {
    if (this.featureTimer) clearInterval(this.featureTimer);
    this.featureTimer = setInterval(() => {
      this.activeFeature = (this.activeFeature + 1) % this.features.length;
    }, FEATURE_SLIDE_INTERVAL_MS);
  }

  goToPage(route: string) {
    this.route.navigateByUrl(route);
  }

  async login() {
    this.errorMessage = '';
    const res = await this.auth.login('/auth/login', { loginId: this.loginId, password: this.password });

    if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
      this.appState.setProfile({
        name: res.Data.name,
        username: res.Data.username,
        role: res.Data.role,
        language: res.Data.language || 'en',
        assignments: res.Data.assignments || [],
        functionalities: res.Data.functionalities || [],
      });
      this.goToPage('/dashboard');
    } else if (res.errorCode === VW_ERROR_CODES.MFA_REQUIRED) {
      // TODO: route to an OTP entry screen once one exists, passing res.Data.mfaSessionId
      this.errorMessage = 'OTP verification required.';
    } else {
      this.errorMessage = res.errorDescription;
    }
  }
}
